import { ModelsResponse, RosterModel, RosterOption } from './types';

/**
 * Model, thinking effort and step budget (chat-ui-spec §2.6). The bar is hidden, but each turn
 * still loads, remembers and sends them exactly as the Query Tool's page does, so showing it later
 * is a flag, not a rebuild.
 */

export type RunOptionKind = 'model' | 'effort' | 'steps';

export interface RunOptions {
  model: string;
  effort: string;
  steps: string;
}

/** Empty values: nothing is sent and the server applies its defaults (the roster failed). */
export const NO_RUN_OPTIONS: RunOptions = { model: '', effort: '', steps: '' };

export const RUN_OPTION_KEYS: Record<RunOptionKind, string> = {
  model: 'centerline-ai.model',
  effort: 'centerline-ai.effort',
  steps: 'centerline-ai.steps',
};

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** localStorage, or nothing when the browser refuses it (private mode, blocked site data). */
export function browserStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

const read = (storage: StorageLike | null, key: string): string | null => {
  try {
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
};

export function saveRunOption(
  storage: StorageLike | null,
  kind: RunOptionKind,
  value: string,
): void {
  try {
    storage?.setItem(RUN_OPTION_KEYS[kind], value);
  } catch {
    // Remembering the choice is a convenience; the turn still sends it.
  }
}

export const rosterFor = (roster: ModelsResponse, kind: RunOptionKind): RosterOption[] => {
  if (kind === 'model') return roster.models ?? [];
  if (kind === 'effort') return roster.efforts ?? [];
  return roster.steps ?? [];
};

/** A saved value is used only while the roster still offers it; otherwise the default. */
export function resolveRunOptions(roster: ModelsResponse, storage: StorageLike | null): RunOptions {
  const pick = (kind: RunOptionKind): string => {
    const saved = read(storage, RUN_OPTION_KEYS[kind]);
    const offered = rosterFor(roster, kind).map((o) => String(o.id));
    if (saved !== null && offered.includes(saved)) return saved;
    const fallback = roster.defaults?.[kind];
    return fallback === undefined || fallback === null ? '' : String(fallback);
  };
  return { model: pick('model'), effort: pick('effort'), steps: pick('steps') };
}

/** The request fields; empty values are left out. */
export function runOptionsPayload(options: RunOptions): Partial<Record<RunOptionKind, string>> {
  const out: Partial<Record<RunOptionKind, string>> = {};
  (Object.keys(RUN_OPTION_KEYS) as RunOptionKind[]).forEach((kind) => {
    if (options[kind]) out[kind] = options[kind];
  });
  return out;
}

const dollars = (n: number) => `$${n < 10 ? n.toFixed(2) : n.toFixed(0)}`;

/**
 * The price hint and its tooltip: `$0.20 in / $1.20 out per 1M tokens — answers quickly, thinks
 * briefly`. Null when the model is not on the roster.
 */
export function priceHint(
  roster: ModelsResponse,
  options: RunOptions,
): { text: string; title: string } | null {
  const model: RosterModel | undefined = roster.models?.find((m) => m.id === options.model);
  if (!model?.price?.short) return null;
  const effort = roster.efforts?.find((e) => e.id === options.effort);
  const { short, long } = model.price;
  const text = `${dollars(short.input)} in / ${dollars(short.output)} out per 1M tokens${
    effort ? ` — ${effort.blurb.toLowerCase()}` : ''
  }`;
  const title = `${
    model.blurb
  }. Over 272k input tokens the whole request is billed at the long-context rate (${dollars(
    long?.input ?? short.input,
  )} in / ${dollars(long?.output ?? short.output)} out).`;
  return { text, title };
}
